import React, { useState, useEffect } from 'react';
import { Calendar, Clock, AlertCircle, Plus, History } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const MyPTODashboard = ({ currentUser, onOpenRequestModal }) => {
  const [balance, setBalance] = useState(null);
  const [recentRequests, setRecentRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchPTOData();
  }, []);

  const fetchPTOData = async () => {
    try {
      setLoading(true);
      const token = sessionStorage.getItem('token');
      
      // Fetch balance
      const balanceRes = await axios.get(`${API_URL}/api/pto/balance`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Fetch recent requests
      const requestsRes = await axios.get(`${API_URL}/api/pto/requests`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setBalance(balanceRes.data.balance);
      setRecentRequests(requestsRes.data.requests.slice(0, 5)); // Latest 5
      setError(null);
    } catch (err) {
      console.error('Error fetching PTO data:', err);
      setError(err.response?.data?.detail || 'Failed to load PTO data');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'approved': return 'bg-green-100 text-green-800';
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'denied': return 'bg-red-100 text-red-800';
      case 'cancelled': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric' 
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (error && !balance) {
    return (
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
        <div className="flex items-center">
          <AlertCircle className="h-5 w-5 text-yellow-600 mr-2" />
          <div>
            <p className="font-medium text-yellow-800">PTO System Not Configured</p>
            <p className="text-sm text-yellow-700">{error}</p>
            <p className="text-sm text-yellow-700 mt-1">Please contact your administrator to set up PTO policies.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">My Time Off</h2>
          <p className="text-sm text-gray-600">Manage your PTO balance and requests</p>
        </div>
        <button
          onClick={onOpenRequestModal}
          className="inline-flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
        >
          <Plus className="h-4 w-4 mr-2" />
          Request Time Off
        </button>
      </div>

      {/* Balance Cards */}
      {balance && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Vacation Balance */}
          <div className="bg-white rounded-lg shadow p-6 border-l-4 border-blue-500">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Vacation</p>
                <p className="text-3xl font-bold text-gray-900 mt-2">
                  {balance.balances.vacation || 0}
                  <span className="text-lg text-gray-600 ml-1">hrs</span>
                </p>
              </div>
              <Calendar className="h-8 w-8 text-blue-500" />
            </div>
          </div>

          {/* Sick Balance */}
          <div className="bg-white rounded-lg shadow p-6 border-l-4 border-red-500">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Sick Leave</p>
                <p className="text-3xl font-bold text-gray-900 mt-2">
                  {balance.balances.sick || 0}
                  <span className="text-lg text-gray-600 ml-1">hrs</span>
                </p>
              </div>
              <AlertCircle className="h-8 w-8 text-red-500" />
            </div>
          </div>

          {/* Personal Balance */}
          <div className="bg-white rounded-lg shadow p-6 border-l-4 border-purple-500">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Personal</p>
                <p className="text-3xl font-bold text-gray-900 mt-2">
                  {balance.balances.personal || 0}
                  <span className="text-lg text-gray-600 ml-1">hrs</span>
                </p>
              </div>
              <Clock className="h-8 w-8 text-purple-500" />
            </div>
          </div>
        </div>
      )}

      {/* Balance Summary */}
      {balance && (
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Balance Summary</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <p className="text-sm text-gray-600">Total Available</p>
              <p className="text-xl font-bold text-gray-900">
                {(balance.balances.vacation + balance.balances.sick + balance.balances.personal).toFixed(2)} hrs
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Used This Year</p>
              <p className="text-xl font-bold text-gray-900">
                {balance.total_used_ytd || 0} hrs
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Accrued This Year</p>
              <p className="text-xl font-bold text-gray-900">
                {balance.total_accrued_ytd || 0} hrs
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Last Accrual</p>
              <p className="text-xl font-bold text-gray-900">
                {balance.last_accrual_date ? formatDate(balance.last_accrual_date) : 'N/A'}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Recent Requests */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-gray-900">Recent Requests</h3>
            <History className="h-5 w-5 text-gray-400" />
          </div>
        </div>
        <div className="divide-y divide-gray-200">
          {recentRequests.length === 0 ? (
            <div className="px-6 py-8 text-center text-gray-500">
              <Calendar className="h-12 w-12 mx-auto text-gray-300 mb-3" />
              <p>No time-off requests yet</p>
              <p className="text-sm mt-1">Click "Request Time Off" to create your first request</p>
            </div>
          ) : (
            recentRequests.map((request) => (
              <div key={request.request_id} className="px-6 py-4 hover:bg-gray-50">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(request.status)}`}>
                        {request.status}
                      </span>
                      <span className="text-sm font-medium text-gray-900 capitalize">
                        {request.pto_type}
                      </span>
                    </div>
                    <div className="mt-2 flex items-center text-sm text-gray-600">
                      <Calendar className="h-4 w-4 mr-1" />
                      {formatDate(request.start_date)} - {formatDate(request.end_date)}
                      <span className="mx-2">•</span>
                      <Clock className="h-4 w-4 mr-1" />
                      {request.total_hours} hours
                    </div>
                    {request.reason && (
                      <p className="mt-1 text-sm text-gray-500">{request.reason}</p>
                    )}
                    {request.review_notes && (
                      <p className="mt-1 text-sm text-gray-700 italic">
                        <span className="font-medium">Admin note:</span> {request.review_notes}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default MyPTODashboard;
